import { UnavailableItemsByName } from '../../../redux/features/menuUnification/menuUnification.props';
import { UnavailabilityReason } from '../../../constants/brandMenu';
import { CartItem } from '../../../utils/cart';
import {
  checkItemInCartItemTree,
  sortModGroups,
  splitPathToItem,
} from '../../../utils/menu';
import { findMatchingPartFromReverse } from '../../../utils';
import { MOD_SEPARATOR } from '../../../utils/constants';
import { ALL_ENTRIES } from '../../../constants';
import {
  isBMItemConstraintNotAvailable,
  isBMItemNotSold,
} from '../../../utils/menuUnification.utils';
import { IModGroup, SelectMenuItemModifier } from './modGroup.props';
import { ParsedModifierGroup } from '../../../types/menu';
import { isBMModifierAnUnavailableConstraint } from '../../../utils/menuUnification.utils';
import logger from '../../../utils/logger';

const hasModGroupChildren = (props: IModGroup) => {
  const { modGroup, cartModGroup, cartItem } = props;

  const modChildren = Object.values(cartModGroup?.selectedItems || {}).filter(
    (selectedModifierItem) => {
      const modGroupChildren = sortModGroups(
        modGroup.menuItems[selectedModifierItem.itemId]
      );
      const filteredModGroups = Object.values(modGroupChildren).filter(
        (mod) => !cartItem.modifierGroups[mod.id]
      );
      return filteredModGroups.length;
    }
  );

  return !!modChildren.length;
};

const canShowBMItem = ({
  unavailableItemsByName,
  itemName,
  cartItem,
  modGroupName,
}: {
  unavailableItemsByName: UnavailableItemsByName;
  itemName: string;
  cartItem: CartItem;
  modGroupName: string;
}) => {
  // If the item is globally not sold, hide it
  if (isBMItemNotSold(unavailableItemsByName, itemName)) {
    return false;
  }
  // If the item is not sold due to a constraint, hide it
  if (
    isBMItemConstraintNotAvailable({
      unavailableItemsByName,
      itemName,
      cartItem,
      constraintName: modGroupName,
      unavailabilityReason: UnavailabilityReason.notSold,
    })
  ) {
    return false;
  }
  // Otherwise, show the item
  return true;
};

/**
 * Determines if a menu item should be shown based on brand menu logic and item availability.
 */
const canShowItem = ({
  isBrandMenu,
  unavailableItemsByName,
  itemName,
  cartItem,
  modGroupName,
  childAvailability,
  is86edToday,
}: {
  isBrandMenu: boolean;
  unavailableItemsByName: UnavailableItemsByName;
  itemName: string;
  cartItem: CartItem;
  modGroupName: string;
  childAvailability: boolean;
  is86edToday: boolean;
}) => {
  if (isBrandMenu) {
    return canShowBMItem({
      unavailableItemsByName,
      itemName,
      cartItem,
      modGroupName,
    });
  }
  // For non-brand menu, show if available or 86ed today
  return !!(childAvailability || is86edToday);
};

const describeLinkedSelections = (
  unavailableItemsByName: UnavailableItemsByName,
  entryName: string,
  owner: CartItem | undefined,
  modGroupName: string
): string => {
  const rules = [
    ...(unavailableItemsByName[entryName] || []),
    ...(unavailableItemsByName[ALL_ENTRIES] || []),
  ];
  const linkedGroupNames = new Set<string>();
  rules.forEach(({ constraints }) => {
    if (!constraints?.[modGroupName]) return;
    Object.keys(constraints).forEach((name) => {
      if (name !== modGroupName) linkedGroupNames.add(name);
    });
  });
  if (!linkedGroupNames.size) return '';
  return Object.values(owner?.childModifierGroups || {})
    .filter((group) => linkedGroupNames.has(group.name))
    .map((group) => {
      const names = Object.values(group.selectedItems || {})
        .map((item) => item.name)
        .sort();
      return `${group.name}=${names.join(',')}`;
    })
    .sort()
    .join('|');
};

/**
 * HITL-1698 — the selections in OTHER groups that decide which of this group's
 * options show. A brand-menu rule such as `{Drink: [Tea], Drink Size: [Large]}`
 * hides a Drink Size option based on the Drink pick, so the size group has to
 * re-render when Drink changes even though its own part of the cart did not.
 * `ModGroup`'s memo compares this string next to the group's own cart node.
 *
 * Covers both owners the option checks look up rules on: the cart item
 * (`canShowItem`) and, for a nested group, the parent option
 * (`isBMModifierAnUnavailableConstraint`). Empty when no rule links the group
 * to another, so unrelated edits still skip the render (HITL-1422).
 */
const getLinkedSelectionsKey = ({
  cartItem,
  pathToItem,
  modGroupName,
  unavailableItemsByName,
}: {
  cartItem: CartItem;
  pathToItem: string;
  modGroupName: string;
  unavailableItemsByName: UnavailableItemsByName;
}): string => {
  const parts = [
    describeLinkedSelections(
      unavailableItemsByName,
      cartItem.name,
      cartItem,
      modGroupName
    ),
  ];
  const nodesInPath = splitPathToItem(pathToItem);
  if (nodesInPath.length > 2) {
    const parentModifier = nodesInPath[nodesInPath.length - 2];
    const parentModifierItem = checkItemInCartItemTree({
      cartItem,
      pathToItem: findMatchingPartFromReverse(
        parentModifier,
        pathToItem,
        MOD_SEPARATOR
      ),
      fromSelectModifier: true,
    });
    if (parentModifierItem) {
      parts.push(
        describeLinkedSelections(
          unavailableItemsByName,
          parentModifier,
          parentModifierItem,
          modGroupName
        )
      );
    }
  }
  return parts.join('#');
};

// Shown instead of speaking the heading's description when express mode has
// suppressed this upsell prompt. A silently dead label would read as broken
// TTS and invite a second click, so say why the click did nothing (HITL-1529).
const EXPRESS_SUPPRESSED_LABEL_TOOLTIP =
  'Express mode is ON — this upsell is suppressed, so its description is not read out.';

// HITL-1720: same idea when an option the prompt offers is 86'd.
const UNAVAILABLE_SUPPRESSED_LABEL_TOOLTIP =
  "An option this prompt offers is 86'd — it is not asked, so its description is not read out.";

export {
  hasModGroupChildren,
  canShowItem,
  canShowBMItem,
  getLinkedSelectionsKey,
  EXPRESS_SUPPRESSED_LABEL_TOOLTIP,
  UNAVAILABLE_SUPPRESSED_LABEL_TOOLTIP,
};

/**
 * Applies a group's menu defaults, as a selection the AI and the agent both
 * see as "nobody chose this". The caller decides WHEN: ModGroup runs it from
 * its mount effect while the cart has no entry for the group yet, and the
 * repeated-slot panel runs it per slot under the same condition, so a set
 * drawn as a panel builds exactly as its rows would have.
 *
 * Moved verbatim out of ModGroup's effect.
 */
export const applyModGroupDefaults = ({
  cartItem,
  modGroup,
  pathToItem,
  parentModifier,
  isBrandMenu,
  unavailableItemsByName,
  selectMenuItemModifier,
}: {
  cartItem: CartItem;
  modGroup: ParsedModifierGroup;
  pathToItem: string;
  parentModifier: string;
  isBrandMenu: boolean;
  unavailableItemsByName: UnavailableItemsByName;
  selectMenuItemModifier: SelectMenuItemModifier;
}) => {
  modGroup.defaultSelectedItemIds?.forEach((itemId: string | number) => {
    if (
      isBrandMenu &&
      isBMModifierAnUnavailableConstraint(
        cartItem,
        unavailableItemsByName,
        parentModifier,
        pathToItem,
        modGroup.name,
        String(itemId)
      )
    ) {
      // Skip: this brand-menu default item is an unavailable constraint.
    } else {
      const matchingItems = Object.values(modGroup.menuItems).filter(
        (item) => item.itemId === String(itemId)
      );

      if (!matchingItems.length) {
        logger.warn({
          message: `Warning: Default item ${itemId} not found in modifier group ${modGroup.id} for cart item ${cartItem.name}`,
        });
      } else if (matchingItems.length > 1) {
        logger.warn({
          message: `Warning: Multiple items found with itemId ${itemId} in modifier group ${modGroup.id} for cart item ${cartItem.name}`,
        });
      }

      // HITL-1708: an option 86'd or meal-period inactive everywhere is
      // never a default — same rule as add time (processDefaultSelectedModifiers).
      matchingItems
        .filter((item) => item.available !== false)
        .forEach((item) =>
          selectMenuItemModifier(item, modGroup, pathToItem, true, false, true)
        );
    }
  });
};
